import { pick, isEqual, pickBy, mapValues, get, values, compact, orderBy, keys, assign, has, map,
  remove, memoize, filter, partition } from 'lodash';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { getIn } from 'icepick';
import { getRegCart } from './shared';
import {
  getUnRegisteredSessions as getUnRegisteredSessionsFromRegCart,
  getRegistrationTypeId as getRegistrationTypeIdFromRegCart,
  getGuestsOfRegistrant
} from '../registrationForm/regCart/selectors';
import { getEventRegistrationId, getSelectedSessionsFromAllRegistrant, getAdmissionItemInfoForPrimaryAndGuests,
  getRegistrationTypeIdForAgenda, getSelectedSessions } from './currentRegistrant';
import { getInitialRegistrationTypeId } from '../userSession';
import { getSessions as getSessionsFromRegCart } from '../registrationForm/regCart/selectors';
import { sessionStatus } from 'event-widgets/redux/selectors/event';

function getEventRegistrationsFromState(state) {
  return getIn(state, ['registrationForm', 'regCart', 'eventRegistrations']);
}

/**
 * get the visible products sort key from state by event registration id
 */
const getVisibleProductsSortKeys = (visibleProducts, eventRegistrationId) => (
  get(visibleProducts, [eventRegistrationId, 'sortKeys'])
);

const getEventRegistrationForPrimaryRegistrant = createSelector(
  getRegCart, getEventRegistrationId, getFilteredEventRegistrationById);

const getFilteredEventRegistration = (eventReg) => {
  let filteredEventRegistration = pick(eventReg,
    ['attendeeType', 'primaryRegistrationId', 'requestedAction', 'displaySequence',
      'eventRegistrationId', 'productRegistrations', 'sessionRegistrations', 'registrationTypeId']);
  filteredEventRegistration = {
    ...filteredEventRegistration,
    attendee: {
      personalInformation: {
        firstName: getIn(eventReg, ['attendee', 'personalInformation', 'firstName']),
        lastName: getIn(eventReg, ['attendee', 'personalInformation', 'lastName'])
      }
    }
  };
  return filteredEventRegistration;
};

function getFilteredEventRegistrationById(regCart, eventRegistrationId) {
  if (!eventRegistrationId) {
    return undefined;
  }

  const eventReg = (regCart && regCart.eventRegistrations) ?
    regCart.eventRegistrations[eventRegistrationId] : {};
  return getFilteredEventRegistration(eventReg);
}

const getEventRegistrationsForCurrentRegistrantAndGuests = createSelector(
  getEventRegistrationsFromState,
  (eventRegs) => {
    if (!eventRegs) {
      return undefined;
    }

    const filteredEventRegistrations = {};
    Object.values(eventRegs).forEach((eventReg) => {
      filteredEventRegistrations[eventReg.eventRegistrationId] =
        getFilteredEventRegistration(eventReg);
    });
    return filteredEventRegistrations;
  }
);

const createGetCurrentRegistrantAndGuestsSelector = createSelectorCreator(
  defaultMemoize,
  isEqual,
  isEqual
);

function getRegistrantSessionsByEventRegId(regCart, eventRegistrationId) {
  if (!eventRegistrationId) {
    return undefined;
  }

  const eventReg = (regCart && regCart.eventRegistrations) ?
    regCart.eventRegistrations[eventRegistrationId] : {};
  return pick(eventReg, ['sessionRegistrations']);
}

const getRegistrantSessionsByEventRegIdSelector = createSelector(
  getRegCart, getEventRegistrationId, getRegistrantSessionsByEventRegId);

const createGetUnRegisteredSessionsSelector = createSelectorCreator(
  defaultMemoize,
  isEqual
);

const getGuestsEventRegistrationId = createSelector(
  getEventRegistrationId,
  getRegCart,
  (primaryEventRegistrationId, regCart) => {
    if (regCart && regCart.eventRegistrations) {
      const filteredGuests = [];
      Object.values(regCart.eventRegistrations).forEach((eventReg) => {
        const isGuest = eventReg.attendeeType === 'GUEST' &&
          eventReg.primaryRegistrationId === primaryEventRegistrationId;
        const isRequestedActionRegister = eventReg.requestedAction &&
          eventReg.requestedAction === 'REGISTER';
        if (isGuest && isRequestedActionRegister) {
          filteredGuests.push(pick(eventReg, ['eventRegistrationId', 'displaySequence']));
        }
      });
      return filteredGuests.sort((first, second) => first.displaySequence - second.displaySequence);
    }
    return undefined;
  }
);

const createGetPrimaryAndGuestVisibleEventRegistrationSelector = createSelectorCreator(
  defaultMemoize,
  isEqual
);

const createGetPrimaryAndGuestSortedVisibleSessionsSelector = createSelectorCreator(
  defaultMemoize,
  isEqual
);

/**
 * gets the current event registrant and the guests associated to the current registrant
 */
export const getCurrentRegistrantAndGuests = createGetCurrentRegistrantAndGuestsSelector(
  getEventRegistrationsForCurrentRegistrantAndGuests,
  getEventRegistrationForPrimaryRegistrant,
  (eventRegistrations, primary) => {
    if (eventRegistrations) {
      const filteredGuests = [];
      Object.values(eventRegistrations).forEach((eventReg) => {
        const isGuest = eventReg.attendeeType === 'GUEST' &&
          eventReg.primaryRegistrationId === primary.eventRegistrationId;
        const isRequestedActionRegister = eventReg.requestedAction &&
          eventReg.requestedAction === 'REGISTER';
        if (isGuest && isRequestedActionRegister) {
          filteredGuests.push(eventReg);
        }
      });
      const primarysGuests = filteredGuests.sort((first, second) => first.displaySequence - second.displaySequence);
      return [primary, ...primarysGuests];
    }
    return [primary];
  }
);

export const getPrimaryAndGuestSelectedSessions = createSelector(
  getCurrentRegistrantAndGuests, getSelectedSessionsFromAllRegistrant);

/**
 * Returns session info for primary + guests in the form
 * {
 *  sessionId: {
 *    registered: [{
 *      eventRegistrationId,
 *      attendee: regcart.eventRegistrations[id].attendee
 *    }],
 *    associated: [{
 *      eventRegistrationId,
 *      attendee: regcart.eventRegistrations[id].attendee
 *    }],
 *    unRegistered: [{
 *      eventRegistrationId,
 *      attendee: regcart.eventRegistrations[id].attendee
 *    }]
 *  }
 * }
 * This is needed for updating the session widget button text and the remaining capacity count.
 * Note: a registered session can be in registered or associated
 */
export const getSessionInfoForPrimaryAndGuests = createSelector(
  // Selector arguments
  getCurrentRegistrantAndGuests,

  // Result function
  (primaryAndGuestEventRegs) => {
    const sessionInfo = {};
    if (!primaryAndGuestEventRegs) {
      return sessionInfo;
    }
    primaryAndGuestEventRegs.forEach((eventReg) => {
      const allSessions = (eventReg && eventReg.sessionRegistrations) || {};
      const registeredSessions = pickBy(allSessions, session => session.requestedAction === 'REGISTER');
      const unregisteredSessions = pickBy(allSessions, session => session.requestedAction === 'UNREGISTER');
      const associatedSessions = pickBy(registeredSessions, s => s.registrationSourceType === 'AdmissionItem');
      const reg = {
        eventRegistrationId: eventReg.eventRegistrationId,
        attendee: eventReg.attendee
      };
      Object.keys({ ...registeredSessions, ...unregisteredSessions }).forEach((sessionId) => {
        // create the empty object if this is the first time we are seeing this session id.
        if (!sessionInfo.hasOwnProperty(sessionId)) {
          sessionInfo[sessionId] = {
            registered: [],
            associated: [],
            unRegistered: []
          };
        }
        // place the event reg in the respective bucket.
        if (associatedSessions[sessionId]) {
          sessionInfo[sessionId].associated.push(reg);
        } else if (registeredSessions[sessionId]) {
          sessionInfo[sessionId].registered.push(reg);
        } else if (unregisteredSessions[sessionId]) {
          sessionInfo[sessionId].unRegistered.push(reg);
        }
      });
    });
    return sessionInfo;
  });

/**
 * Returns true if primary and atleast one of their guests have differing session or admission item selections
 * Checks the number of party members registered for each product and returns false if the
 * number of registrations for a product is different than the total number of guests
 */
export const primaryAndGuestsHaveDifferentAgendas = createSelector(
  // Selector arguments
  getRegCart,
  getEventRegistrationId,
  getSessionInfoForPrimaryAndGuests,
  getAdmissionItemInfoForPrimaryAndGuests,

  // Result Function
  (regCart, primaryRegId, sessionInfoForPrimaryAndGuests, admissionItemInfoForPrimaryAndGuests) => {
    const guestCount = getGuestsOfRegistrant(regCart, primaryRegId).length || 0;
    let haveDifferingSessions = false;
    Object.keys(sessionInfoForPrimaryAndGuests).forEach((sessionId) => {
      const session = sessionInfoForPrimaryAndGuests[sessionId];
      const registeredSessionCounts = session ? session.registered.length + session.associated.length :
        0;
      // 1 is added to guestCount to include the primary
      if (guestCount > 0 && registeredSessionCounts !== (guestCount + 1)) {
        haveDifferingSessions = true;
      }
    });
    let haveDifferingAdmissionItems = false;
    Object.keys(admissionItemInfoForPrimaryAndGuests).forEach((admissionItemId) => {
      const admissionItem = admissionItemInfoForPrimaryAndGuests[admissionItemId];
      // 1 is added to guestCount to include the primary
      if (guestCount > 0 && admissionItem.registered.length > 0 &&
        admissionItem.registered.length !== (guestCount + 1)) {
        haveDifferingAdmissionItems = true;
      }
    });
    return haveDifferingSessions || haveDifferingAdmissionItems;
  });

export const getUnRegisteredSessions = createGetUnRegisteredSessionsSelector(
  getRegistrantSessionsByEventRegIdSelector,
  getUnRegisteredSessionsFromRegCart);

/**
 * get the visible event registration for primary and guest session groups
 */
export const getPrimaryAndGuestVisibleEventRegistration = createGetPrimaryAndGuestVisibleEventRegistrationSelector(
  getGuestsEventRegistrationId,
  (state) => state.visibleProducts.Sessions,
  getEventRegistrationId,
  (guestEventRegistrationIds, visibleProducts, eventRegistrationId) => {
    const visibleEventRegistrationForPrimaryAndGuests = mapValues(get(visibleProducts, [eventRegistrationId, 'sessionProducts']), session => {
      if (session.type !== 'Session') {
        return { visibleEventReg: [eventRegistrationId] };
      }
    });
    guestEventRegistrationIds.forEach((eventReg) => {
      const guestEventRegistrationId = eventReg.eventRegistrationId;
      const guestSessions = values(get(visibleProducts, [guestEventRegistrationId, 'sessionProducts']));
      guestSessions.forEach((guestSession) => {
        const primaryGuestSession = visibleEventRegistrationForPrimaryAndGuests[guestSession.id];
        if (!primaryGuestSession) {
          if (guestSession.type !== 'Session') {
            visibleEventRegistrationForPrimaryAndGuests[guestSession.id] = {
              visibleEventReg: [guestEventRegistrationId]
            };
          }
        } else if (guestSession.type !== 'Session') {
          if (!primaryGuestSession.visibleEventReg.find(eventRegId => eventRegId === guestEventRegistrationId)) {
            primaryGuestSession.visibleEventReg.push(guestEventRegistrationId);
          }
        }
      });
    });
    return visibleEventRegistrationForPrimaryAndGuests;
  }
);

/**
 * get the sorted visible admission items for the current registrant without guest products
 */
export const getPrimarySortedVisibleAdmissionItems = createSelector(
  getEventRegistrationId,
  state => state.visibleProducts.Sessions,
  (eventRegistrationId, visibleProducts) => (get(visibleProducts, [eventRegistrationId, 'admissionItems'])));

/**
 * get the merged sorted admission items for the current registrant and guests
 */
export const getPrimaryAndGuestSortedVisibleAdmissionItems = createSelector(
  getEventRegistrationId,
  getInitialRegistrationTypeId,
  getRegCart,
  state => state.visibleProducts.Sessions,
  (primaryRegId,
    initialRegTypeId,
    regCart,
    visibleProducts
  ) => {
    const primaryEventRegTypeId = getRegistrationTypeIdFromRegCart(regCart, primaryRegId);
    const allGuestEventRegs = getGuestsOfRegistrant(regCart, primaryRegId, null);
    const visibleProductsForPrimaryAndGuests = { ...get(visibleProducts, [primaryRegId, 'admissionItems']) };

    // If guest can select their own reg type and guests exist
    allGuestEventRegs.forEach((eventReg) => {
      const eventRegId = eventReg.eventRegistrationId;
      // Get selected registration type information for this registrant
      const registrationTypeId = eventReg.registrationTypeId || initialRegTypeId;
      // If guest registration type id is not the same as the primary, merge the admission items.
      if (visibleProducts[eventRegId] && (registrationTypeId !== primaryEventRegTypeId)) {
        Object.values(visibleProducts[eventRegId].admissionItems).forEach(admissionItem => {
          // If guest admission item isn't in visible products, add it.
          if (!visibleProductsForPrimaryAndGuests[admissionItem.id]) {
            visibleProductsForPrimaryAndGuests[admissionItem.id] = { ...admissionItem };
          }
        });
      }
    });
    return visibleProductsForPrimaryAndGuests;
  });

/**
 * get the sorted visible sessions for the current registrant without guest products
 */
export const getPrimarySortedVisibleSessions = createSelector(
  (state) => state.visibleProducts.Sessions,
  getEventRegistrationId,
  (visibleProducts, eventRegistrationId) => (
    values(get(visibleProducts, [eventRegistrationId, 'sessionProducts']))
  ));

/**
 * get the sorted visible sessions for the given event registration id
 */
export const getSortedVisibleSessionsForEventRegistration = (visibleProducts, eventRegistrationId) => {
  let sessions = get(visibleProducts, ['Sessions', eventRegistrationId, 'sessionProducts']);
  let sortedVisibleSessions = [];
  if (sessions) {
    const partitionedSessions = partition(sessions, session => { return session.type !== 'Session' && session.sessions; });
    // adds sessions to sortedVisibleSessions
    Object.keys(partitionedSessions[1]).forEach(key => {
      sortedVisibleSessions[partitionedSessions[1][key].id] = { name: partitionedSessions[1][key].name };
    });
    const groups = partitionedSessions[0];
    // add sessions from session groups to sortedVisibleSessions
    groups.forEach(group => {
      Object.keys(group.sessions).forEach(groupSessionId => {
        sortedVisibleSessions[groupSessionId] = { name: group.sessions[groupSessionId].name };
      });
    });
  }
  return sortedVisibleSessions;
};

export const getSessionsVisibleToPrimary = createSelector(
  getPrimarySortedVisibleSessions,
  (sessions) => {
    let visibleSessions = [];
    sessions.forEach((session) => {
      visibleSessions.push(session.id);
      if (session.sessions) {
        visibleSessions = visibleSessions.concat(map(session.sessions, sessionInGroup => sessionInGroup.id));
      }
    });
    return visibleSessions;
  }
);

/**
 * get the merged sorted visible sessions for the current registrant and guests
 */
export const getPrimaryAndGuestSortedVisibleSessions = createGetPrimaryAndGuestSortedVisibleSessionsSelector(
  getGuestsEventRegistrationId,
  (state) => state.visibleProducts.Sessions,
  getEventRegistrationId,
  (guestEventRegistrationIds, visibleProducts, eventRegistrationId) => {
    const visibleProductsForPrimaryAndGuests = values(get(visibleProducts,
      [eventRegistrationId, 'sessionProducts']));
    const primaryAndGuestSortKeys = { ...getVisibleProductsSortKeys(visibleProducts, eventRegistrationId) };
    guestEventRegistrationIds.forEach((eventReg) => {
      const guestEventRegistrationId = eventReg.eventRegistrationId;
      const guestSessions = values(get(visibleProducts,
        [guestEventRegistrationId, 'sessionProducts']));
      const guestSortKeys = getVisibleProductsSortKeys(visibleProducts, guestEventRegistrationId);
      guestSessions.forEach((guestSession) => {
        const primaryGuestSessionIndex = visibleProductsForPrimaryAndGuests
          .findIndex(session => session.id === guestSession.id);
        if (primaryGuestSessionIndex === -1) {
          // guest session doesn't exist, add it
          visibleProductsForPrimaryAndGuests.push(guestSession);
          assign(primaryAndGuestSortKeys, { [guestSession.id]: guestSortKeys[guestSession.id] });
        } else if (guestSession.type !== 'Session') {
          const primaryGuestSession = visibleProductsForPrimaryAndGuests[primaryGuestSessionIndex];
          keys(guestSession.sessions).forEach((guestSessionInGroupId) => {
            // add guest sessions within session group
            if (!has(primaryGuestSession.sessions, guestSessionInGroupId)) {
              visibleProductsForPrimaryAndGuests[primaryGuestSessionIndex] = {
                ...primaryGuestSession,
                sessions: {
                  ...primaryGuestSession.sessions,
                  [guestSessionInGroupId]: guestSession.sessions[guestSessionInGroupId]
                }
              };
            }
          });
        }
      });
    });
    // sort sessions
    const sortedKeys = orderBy(values(map(primaryAndGuestSortKeys, (v, k) => ({ id: k, sortKey: v }))),
      ['sortKey'], ['asc']);
    return compact(map(sortedKeys, (sortedKey) =>
      visibleProductsForPrimaryAndGuests.find((session) => session.id === sortedKey.id)));
  });

const getAllSortedSessionsSelector = createSelector(
  getRegistrationTypeIdForAgenda,
  state => state.visibleProducts,
  (calculatedRegistrationTypeId, visibleProducts) => memoize((
    widgetType, widgetId = null, limitByRegistrationType = false) => {
    const widget = widgetId != null ? `${widgetType}:${widgetId}` : widgetType;
    let sessionProducts;
    if (limitByRegistrationType) {
      sessionProducts = values(get(visibleProducts,
        [widget, calculatedRegistrationTypeId, 'sessionProducts']));
    } else {
      sessionProducts = values(get(visibleProducts,
        [widget, 'sessionProducts']));
    }
    return remove(sessionProducts, product => !product.sessions)
      .filter(session => session.status !== sessionStatus.cancelled);
  })
);

export const getAllSortedSessions = (state, widgetType, widgetId, limitByRegistrationType) =>
  (getAllSortedSessionsSelector(state)(widgetType, widgetId, limitByRegistrationType));

export const getAllSortedSessionsForWidget = (state) =>
  (getAllSortedSessionsSelector(state)('Widget'));

const getAllSortedAdmissionItemsSelector = createSelector(
  getRegistrationTypeIdForAgenda,
  state => state.visibleProducts,
  (calculatedRegistrationTypeId, visibleProducts) => memoize((widgetType, widgetId = null,
    limitByRegistrationType = false) => {
    const widget = widgetId != null ? `${widgetType}:${widgetId}` : widgetType;
    if (limitByRegistrationType) {
      return get(visibleProducts,
        [widget, calculatedRegistrationTypeId, 'admissionItems']);
    }
    return get(visibleProducts, [widget, 'admissionItems']);
  })
);
export const getAllSortedAdmissionItems = (state, widgetType, widgetId, limitByRegistrationType) =>
  (getAllSortedAdmissionItemsSelector(state)(widgetType, widgetId, limitByRegistrationType));

export const getAllSortedAdmissionItemsForWidget = (state) =>
  (getAllSortedAdmissionItemsSelector(state)('Widget'));

export const getAllSortedSessionsForPrimaryAndGuest = createSelector(
  getPrimaryAndGuestSortedVisibleSessions,
  sessionAndSessionGroups => {
    let sessions = [];
    sessionAndSessionGroups.forEach(sessionOrSessionGroup => {
      if (sessionOrSessionGroup.sessions) {
        sessions = sessions.concat(values(sessionOrSessionGroup.sessions));
      } else {
        sessions.push(sessionOrSessionGroup);
      }
    });
    return sessions;
  }
);

/**
 * Gets the snapshot definition of the current registrants selected sessions if one is available.
 */
export const getSelectedSessionDefinitions = (state, widgetType = 'Sessions', widgetId) => {
  let sessions;
  switch (widgetType) {
    case 'Sessions':
      sessions = values(getAllSortedSessionsForPrimaryAndGuest(state));
      break;
    case 'InviteeAgenda':
    default:
      sessions = getAllSortedSessions(state, widgetType, widgetId);
      break;
  }
  const selectedSessions = getSelectedSessions(state);
  return sessions.filter(session => has(selectedSessions, session.id));
};


/**
 * Gets the snapshot definition of the current registrants guests selected sessions if one is available.
 */
export const getGuestSelectedSessionDefinitions = createSelector(
  getAllSortedSessionsForPrimaryAndGuest,
  getRegCart,
  getEventRegistrationId,
  (sessions, regCart, primaryRegId) => {
    const allGuestEventRegs = getGuestsOfRegistrant(regCart, primaryRegId, null);
    return sessions.filter(session => {
      let isSessionRegisteredFor;
      allGuestEventRegs.forEach((eventReg) => {
        const registeredSessions = getSessionsFromRegCart(regCart, eventReg.eventRegistrationId);
        isSessionRegisteredFor = has(registeredSessions, session.id);
      });
      return isSessionRegisteredFor;
    });
  });

/**
 * Gets snapshot definition of the current registrants guests selected sessions separated by guest
 */
export const getGuestSelectedSessionDefinitionsByGuest = createSelector(
  getAllSortedSessionsForPrimaryAndGuest,
  getRegCart,
  getEventRegistrationId,
  (sessions, regCart, primaryRegId) => {
    const sessionsMap = new Map(sessions.map(session => [session.id, session]));
    const allGuestEventRegs = getGuestsOfRegistrant(regCart, primaryRegId, null);
    const guestSessionSelectionMap = new Map();
    allGuestEventRegs.forEach((eventReg) => {
      const registeredSessions = getSessionsFromRegCart(regCart, eventReg.eventRegistrationId);
      let savedValue = [];
      let sessionInfo = [];
      for (let session in registeredSessions) {
        if (session != null) {
          sessionInfo = registeredSessions[session];
          if (sessionInfo.requestedAction === 'REGISTER') {
            savedValue = guestSessionSelectionMap.get(eventReg.eventRegistrationId) || [];
            // Only conditionally set values into guestSessionSelectionMap to avoid lists like [undefined];
            const guestSessionSelectionValue = [...savedValue,
              ...(sessionsMap.get(session) ? [sessionsMap.get(session)] : [])];
            guestSessionSelectionMap.set(eventReg.eventRegistrationId, guestSessionSelectionValue);
          }
        }
      }
    });
    return guestSessionSelectionMap;
  });

export const getSessionGroups = createSelector(
  getPrimaryAndGuestSortedVisibleSessions,
  sessionAndSessionGroups => filter(values(sessionAndSessionGroups), sessionGroup => sessionGroup.sessions)
);
